import React, { useEffect, useState } from 'react';
import { Card, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Domain as DomainIcon } from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineNumberField from '../../../common/inline/InlineNumberField';
import SupplierSelector from '../../supplier/components/SupplierSelector';
import {
  BaseSporCardProps,
  Supplier,
  SupplierPurchaseOrder
} from '../../supplier/Supplier.interface';
import { useUpdateSpor } from '../../supplier/Supplier.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const SupplierDetailsFormCard: React.FC<BaseSporCardProps> = (
  props: BaseSporCardProps
) => {
  const { spor, onChange, onUpdate, onError } = props;
  const { supplierId } = spor;
  const [supplier, setSupplier] = useState<Partial<Supplier> | undefined>(
    supplierId ? { id: supplierId } : undefined
  );
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Spor>(
    spor.task
  );
  const { updatedSpor, isUpdating, updateError, updateSpor } = useUpdateSpor(
    spor.id
  );
  const initialValues: Partial<Supplier> = {
    ...supplier,
    address: supplier?.address || '',
    contactName: supplier?.contactName || '',
    phoneNumber: supplier?.phoneNumber || ''
  };

  useEffect(() => {
    setSupplier({ ...supplier, id: supplierId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  useEffect(() => {
    // only update if supplier.id changed
    if (supplier && supplierId !== supplier.id) {
      const dto: Partial<SupplierPurchaseOrder> = { supplierId: supplier.id };
      updateSpor(dto);
      onChange?.(dto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  useEffect(() => {
    if (updatedSpor) {
      onChange?.(updatedSpor);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSpor]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <DomainIcon />
        Section 2: Supplier Details
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={() => {}}
        >
          {({ values, setFieldValue }) => (
            <InlineForm>
              <Grid item xs={12}>
                <SupplierSelector
                  value={values.id}
                  onSelected={setSupplier}
                  disabled={isFieldDisabled('supplierId')}
                  onChange={(supplier: Supplier | null) => {
                    setFieldValue('supplierId', supplier?.id, true);
                    setSupplier(supplier || undefined);
                  }}
                />
              </Grid>
              {values.id && (
                <>
                  <Grid item xs={12}>
                    <Alert severity='info'>
                      Additional Supplier information:
                    </Alert>
                  </Grid>

                  <InlineTextField
                    name='contactName'
                    label='Contact Name'
                    placeholder='Supplier Contact Name is displayed here'
                    inputProps={{ readOnly: true }}
                  />
                  <InlineNumberField
                    name='phoneNumber'
                    label='Phone Number'
                    placeholder='Supplier Phone Number is displayed here'
                    inputProps={{ readOnly: true }}
                  />
                  <InlineTextField
                    name='address'
                    label='Address'
                    placeholder='Supplier Address is displayed here'
                    inputProps={{ readOnly: true }}
                  />
                </>
              )}
            </InlineForm>
          )}
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default SupplierDetailsFormCard;
